import { inject, Injectable } from '@angular/core';
import { pullAt, uniqueId } from '@sama/micro-dash';
import { removeShapesFromGroups } from '../../../cross-team-shared/annotation/shared/state/shape-group-state';
import {
  ShapeGroupState,
  ShapeOrGroupOrderItem,
} from '../../../cross-team-shared/state/shape-group-state';
import { outputAnnotationInfoStoreToken } from '../../../outputs/annotation/annotation-shared/annotation-providers';
import { withInsertedElements } from '../../../shared/misc-utilities/misc-utils';
import { ShapeGroupOrderState } from '../../../team-apis/content/content-connector';
import { Selection } from '../../h3ml/state/fusion-state';
import { ShapeSelectionService } from '../../outputs/annotation/annotation-shared/utils/shape-selection.service';

class OrderInfo {
  newGroupShapeIndexes: number[] = [];
  orderIndexesToRemove: number[] = [];
  orderIndexForNewGroup = 0;
}

@Injectable()
export class ShapeGroupingService {
  private readonly outputAnnotationInfoStoreToken = inject(
    outputAnnotationInfoStoreToken,
  );
  private readonly selectionService = inject(ShapeSelectionService);

  groupShapes(groupType: string, selection?: Selection) {
    const shapeInfo = this.outputAnnotationInfoStoreToken.state();
    // fix me if you change this code
    // eslint-disable-next-line no-param-reassign -- fix me!
    selection ??= this.selectionService.getSelectionForOutput();

    const {
      orderIndexForNewGroup,
      newGroupShapeIndexes,
      orderIndexesToRemove,
    } = getOrderInfoForNewGroup(shapeInfo, selection);

    const newGroup: ShapeGroupState = {
      index: +uniqueId(),
      type: groupType,
      shapeIndexes: newGroupShapeIndexes,
    };

    const updatedGroups = {
      ...removeShapesFromGroups(shapeInfo.groups, newGroupShapeIndexes),
      [newGroup.index]: newGroup,
    };

    this.outputAnnotationInfoStoreToken.getRootStore().batch(() => {
      // @ts-ignore -- fix me
      this.outputAnnotationInfoStoreToken('groups').set(updatedGroups);
      this.outputAnnotationInfoStoreToken('shapeAndGroupOrder').setUsing(
        orderWithNewGroup,
        newGroup.index,
        orderIndexForNewGroup,
        orderIndexesToRemove,
      );
    });

    return newGroup;
  }
}

function getOrderInfoForNewGroup(
  shapeGroupOrder: ShapeGroupOrderState,
  selection: Selection,
) {
  const orderInfo = new OrderInfo();

  for (const [i, item] of shapeGroupOrder.shapeAndGroupOrder.entries()) {
    if (item.type === 'shape') {
      processOrderInfoForShape({
        orderInfo,
        selection,
        shapeIndex: item.index,
        orderIndex: i,
      });
    } else {
      processOrderInfoForGroup({
        orderInfo,
        selection,
        shapeGroupOrder,
        groupIndex: item.index,
        orderIndex: i,
      });
    }
  }

  return orderInfo;
}

function processOrderInfoForShape({
  orderInfo,
  selection,
  shapeIndex,
  orderIndex,
}: {
  orderInfo: OrderInfo;
  selection: Selection;
  shapeIndex: number;
  orderIndex: number;
}) {
  // fix me if you change this code

  if (selection[shapeIndex]) {
    orderInfo.orderIndexesToRemove.push(orderIndex);
    orderInfo.newGroupShapeIndexes.push(shapeIndex);
    orderInfo.orderIndexForNewGroup = orderIndex + 1;
  }
}

function processOrderInfoForGroup({
  orderInfo,
  selection,
  shapeGroupOrder,
  groupIndex,
  orderIndex,
}: {
  orderInfo: OrderInfo;
  selection: Selection;
  shapeGroupOrder: ShapeGroupOrderState;
  groupIndex: number;
  orderIndex: number;
}) {
  const group = shapeGroupOrder.groups[groupIndex];
  let numberOfRemovedShapes = 0;

  // @ts-ignore -- fix me
  for (const shapeIndex of group.shapeIndexes) {
    // fix me if you change this code

    if (selection[shapeIndex]) {
      orderInfo.newGroupShapeIndexes.push(shapeIndex);
      orderInfo.orderIndexForNewGroup = orderIndex + 1;
      numberOfRemovedShapes++;
    }
  }

  // @ts-ignore -- fix me
  if (numberOfRemovedShapes === group.shapeIndexes.length) {
    orderInfo.orderIndexesToRemove.push(orderIndex);
  }
}

function orderWithNewGroup(
  order: ShapeOrGroupOrderItem[],
  newGroupIndex: number,
  newOrderIndex: number,
  orderIndexesToRemove: number[],
) {
  const newOrderItem: ShapeOrGroupOrderItem = {
    type: 'group',
    index: newGroupIndex,
  };
  const withNewGroup = withInsertedElements(order, newOrderIndex, newOrderItem);
  pullAt(withNewGroup, orderIndexesToRemove);
  return withNewGroup;
}
